/**
 * Ported from plm/portal/ui/lib/quickTests/web/legacy/TraceRoute.js
 *
 * Horizontal-only port intended to drive the per-agent Test Summary visualization
 * in react-portal. Differences from the original:
 *  - Converted CommonJS require/module.exports to ESM imports/default export.
 *  - Removed jQuery: $.each -> _.forEach (note arg order), $.* DOM helpers
 *    are not needed because the surrounding React component owns the DOM.
 *  - Removed the jQuery `$(function () { ... })` page/scroll handler block and
 *    related helpers (`addTraceRouteMouseOvers`, `allLoaded`, `disableInfoDivs`,
 *    `columnsThatFitScreen`); React layout owns scrolling and the in-canvas
 *    Kinetic event handlers in `VisualizePath` cover hover/click behavior.
 *  - Kept the vertical-branch dispatch points so that the file stays close to
 *    the original. We only call `buildHorizontal` from the React wrapper.
 */

import _ from 'lodash';
import VisualizePath from './VisualizePath';
import ISPNameSelector from './ISPNameSelector';

function TraceRoute(quickTest, isDarkMode) {
    if (_.isUndefined(quickTest)) throw new Error('Must have quickTest to call new on Trace Route');

    this.quickTest = quickTest;
    this.to_be_loaded_table = {};
    this.someAgentIds = _.map(quickTest.agentResults, 'agent');
    this.num_agents_to_load = this.someAgentIds.length;
    this.numberRendered = 0;
    this.pathWidth = 275;
    this.containerWidth = null;
    this.maxColumns = null;
    this.vertical_detail = null;
    this.horizontal_detail = null;
    this.leftMargin = 30;
    this.rightMargin = 75;
    this.padding = 15;
    this.name_selector = new ISPNameSelector();
    this.isDarkMode = isDarkMode;

    const DEFAULT_GROUPING_LEVEL = 'level_4';
    const DETAIL_LEVEL_1 = 'level_1';

    const me = this;

    this.init = function init() {
        this.populateTraceRouteLoadTable();
        return this;
    };

    this.populateTraceRouteLoadTable = function populateTraceRouteLoadTable() {
        _.forEach(me.someAgentIds, (agentId) => {
            me.to_be_loaded_table[agentId] = false;
        });
    };

    /**
     * Builds the trace route visualization using KineticJS.
     * KEEP THEM IN THIS ORDER OTHERWISE THINGS GET WEIRD.
     */
    this.buildTraceRouteViz = function buildTraceRouteViz(data, agentId) {
        this.buildHorizontal(data, agentId);
    };

    /**
     * We need to know how many hops responded in order to know how many nodes to draw.
     */
    this.calcHopsInPath = function calcHopsInPath(data /* , level */) {
        return _.get(data, 'number_of_orgs', 0);
    };

    /**
     * Builds the summary tab version of the visualization. Here we are only showing
     * the org changes. If the destination is internal however, we will show all hops.
     */
    this.buildHorizontal = function buildHorizontal(data, agentId) {
        this.horizontal_detail = DETAIL_LEVEL_1;

        const nodesInPath = this.calcHopsInPath(data, this.horizontal_detail);

        const v = new VisualizePath(me.quickTest, me).init({
            container: `container-${agentId}-horizontal`,
            maxNodesInPath: nodesInPath || 1,
            destinationMessage: me.quickTest.name,
            pathHeight: 80,
            vertical: false,
            internal: data.internal_destination,
            cloud_probe: data.cloud_probe,
            rootNodeHeight: 100,
            rootNodeWidth: 235,
            destNodeHeight: 100,
            topMargin: 30,
            nodeRadius: 25,
            nodeHorizontalSpace: 75,
            isDarkMode,
        });

        this.renderTraceRouteViz(v, data, agentId, this.horizontal_detail, false);
        return v;
    };

    /**
     * Uses VisualizePath to render a trace route, grouping nodes by `rollup_<detail>` key.
     */
    this.renderTraceRouteViz = function renderTraceRouteViz(v, probe, agentId, levelDetail, vertical) {
        const detail = levelDetail || DEFAULT_GROUPING_LEVEL;
        const rollupGroup = `rollup_${detail}`;
        v.setLevelDetail(detail);

        const nodesInGroup = [];
        let lastRollupNumber = '';

        probe.id = `probe-${agentId}`;

        v.addRootToPath(probe);

        if (probe.error) {
            v._drawFailedTraceRoute(vertical);
        } else {
            v._buildPaths();

            _.forEach(probe.hop_data, (node, nodeIdx) => {
                const details = vertical ? '-details' : '';
                node.id = `probe-${agentId}-node-${nodeIdx}${details}`;
                node.destination_ip = probe.resolved_ip;

                node.rendered = false;

                const rollupNumber = node[rollupGroup];

                if (rollupNumber != null) {
                    if (rollupNumber !== lastRollupNumber) {
                        renderNodesInGroup(v, agentId, nodesInGroup);
                        nodesInGroup.length = 0;
                    }
                    nodesInGroup.push(node);
                    node.rendered = true;
                }
                lastRollupNumber = rollupNumber;
            });

            if (
                probe.hop_data.length > 0 &&
                (nodesInGroup.length === 0 || !probe.hop_data[probe.hop_data.length - 1].rendered)
            ) {
                if (vertical) {
                    nodesInGroup.push(probe.hop_data[0]);
                } else {
                    v._addNodeDetails(
                        probe.hop_data[probe.hop_data.length - 1],
                        v.pathPositions.x + 17,
                        v.pathPositions.y
                    );
                }
            }

            renderNodesInGroup(v, agentId, nodesInGroup);
        }
        v.draw();
    };

    function renderNodesInGroup(v, agentId, someNodes) {
        const hops = someNodes.length;
        if (hops === 0) return;

        const baseNode = someNodes.shift();
        baseNode.hops = hops;

        if (someNodes.length > 0) baseNode['sub-nodes'] = someNodes;

        v.addNodeToPath(agentId, baseNode);
    }
}

export default TraceRoute;
